import ReviewDispatchPanel from '../components/ReviewDispatchPanel';
import './Reviews.css';
// src/pages/Reviews.jsx
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import API from '../services/api';
import {
  RefreshCw, ExternalLink, CheckCircle, Copy, Send, Search, X, Star, AlertCircle, Mail, MessageCircle, Sparkles, Clock3, StickyNote,
} from 'lucide-react';

const STATUS_OPTIONS = ['Bestellt', 'Reviewed', 'Eingereicht', 'Storno', 'Privat'];

const STATUS_BADGE = {
  Bestellt: { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24', label: 'Bestellt' },
  Reviewed: { bg: 'rgba(74,222,128,0.15)', color: '#4ade80', label: 'Reviewed' },
  Eingereicht: { bg: 'rgba(167,139,250,0.15)', color: '#a78bfa', label: 'Eingereicht' },
  Storno: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', label: 'Storno' },
  Privat: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', label: 'Privat' },
};

function formatDate(d) {
  return d ? new Date(d).toLocaleDateString('de-DE') : '-';
}
function clampText(str, max = 90) {
  if (!str) return '-';
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
}
function getAsin(o) {
  const a = (o?.asin || '').trim();
  return a || null;
}
function getProductName(o) {
  return (o?.product_name || o?.productTitle || o?.name || '').trim() || '-';
}
function normalizeName(name) {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}
function toDateMs(d) {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : 0;
}
function isLockedStatus(status) {
  return status === 'Eingereicht' || status === 'Storno' || status === 'Privat';
}

export default function Reviews() {
  const [allOrders, setAllOrders] = useState([]);
  const [activeView,setActiveView]=useState('reviews');
  const [dispatchRequest,setDispatchRequest]=useState(null);
  const [openReviewCount,setOpenReviewCount]=useState(null);
  const [pageError,setPageError]=useState('');
  const requestDispatch=(ids=[],manual=false,all=false)=>{setDispatchRequest({id:Date.now()+Math.random(),ids,manual,all});setActiveView('dispatch');};
  const [accountList, setAccountList] = useState([]);
  const [allAiReviews, setAllAiReviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [importingReviews, setImportingReviews] = useState(false);
  const [showOnlyWithReview, setShowOnlyWithReview] = useState(false);
  const [agentSort, setAgentSort] = useState(false);

  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(['Bestellt', 'Reviewed']);
  const [selectedMain, setSelectedMain] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedOrderId, setCopiedOrderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [noteModal, setNoteModal] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [reviewChangeUndo, setReviewChangeUndo] = useState(null);
  const reviewChangeUndoTimer = useRef(null);

  // KI Modal
  const [aiModal, setAiModal] = useState(null);
  const [aiExtraInfo, setAiExtraInfo] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiExisting, setAiExisting] = useState(null);
  const [aiError, setAiError] = useState('');
  const [aiCopied, setAiCopied] = useState(false);
  // Neu-Generieren Confirm (statt window.confirm)
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  const [regenBulk, setRegenBulk] = useState(true); // true = ganze ASIN-Gruppe
  const [regenInfo, setRegenInfo] = useState('');
  const [regenRunning, setRegenRunning] = useState(false);

  const existingReviewCache = useRef(new Map()); // key -> review|null
  const lastActionAtRef = useRef(0); // simple cooldown gegen Spam/429

  // Group Modal
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupMode, setGroupMode] = useState('asin');
  const [groupAsin, setGroupAsin] = useState(null);
  const [, setGroupNameRaw] = useState('');
  const [groupNameKey, setGroupNameKey] = useState('');
  const [groupTitle, setGroupTitle] = useState('');
  const [groupRange, setGroupRange] = useState({ from: null, to: null });
  const [selectedGroup, setSelectedGroup] = useState({});
  const [groupStatusToSet, setGroupStatusToSet] = useState('Eingereicht');

  useEffect(() => {
    API.get('/accounts').then(res => {
      setAccountList((res.data.accounts || []).map(a => a.account_name));
    }).catch(()=>setPageError('Accounts konnten nicht geladen werden.'));
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const [results, changeRes, aiRes] = await Promise.all([
        Promise.all(STATUS_OPTIONS.map(s => API.get('/orders', { params: { limit: 2000, status: s } }))),
        API.get('/reviews/change-required', { params: { limit: 2000 } }),
        API.get('/reviews', { params: { limit: 1000 } }),
      ]);

      // Review-Änderungen werden absichtlich zusätzlich geladen, damit sie auch
      // bei Statuswerten außerhalb der normalen Review-Filter sichtbar bleiben.
      const normalOrders = results.flatMap(r => r.data.orders || []);
      const flaggedOrders = changeRes.data.orders || [];
      const unique = Object.values(Object.fromEntries(
        [...normalOrders, ...flaggedOrders].map(o => [o.id, o])
      ));
      setAllOrders(unique);

      const aiMap = {};
      for (const r of (aiRes.data.reviews || [])) aiMap[r.order_id] = r;
      setAllAiReviews(aiMap);
    } catch (err) {
      console.error(err);
      setPageError(err.response?.data?.error||'Reviews konnten nicht vollständig geladen werden. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    const onOrdersUpdated = (e) => {
      if (e?.detail?.source === 'reviews') return;
      fetchOrders();
    };
    window.addEventListener('orders-updated', onOrdersUpdated);
    return () => window.removeEventListener('orders-updated', onOrdersUpdated);
  }, [fetchOrders]);

  const copyText = async (text, id = null) => {
    try {
      await navigator.clipboard.writeText(text);
      if (id) { setCopiedId(id); setTimeout(() => setCopiedId(null), 1500); }
    } catch {
      alert('Konnte nicht kopieren.');
    }
  };
  const copyLink = (url, id) => copyText(url, id);

  const setReviewChangeRequired = useCallback(async (orderId, required) => {
    const res = await API.patch(`/reviews/change-required/${orderId}`, { required });
    const updated = res.data.order || {};
    setAllOrders(prev => prev.map(o => Number(o.id) === Number(orderId)
      ? { ...o, ...updated, review_change_required: required }
      : o));
    return updated;
  }, []);

  const completeReviewChange = useCallback(async (order) => {
    try {
      await setReviewChangeRequired(order.id, false);
      if (reviewChangeUndoTimer.current) clearTimeout(reviewChangeUndoTimer.current);
      setReviewChangeUndo({
        id: order.id,
        orderNumber: order.order_number,
        note: order.review_change_note || null,
      });
      reviewChangeUndoTimer.current = setTimeout(() => setReviewChangeUndo(null), 9000);
    } catch (err) {
      alert('Review-Änderung konnte nicht geschlossen werden: ' + (err.response?.data?.error || err.message));
    }
  }, [setReviewChangeRequired]);

  const undoReviewChange = useCallback(async () => {
    if (!reviewChangeUndo?.id) return;
    try {
      await setReviewChangeRequired(reviewChangeUndo.id, true);
      if (reviewChangeUndoTimer.current) clearTimeout(reviewChangeUndoTimer.current);
      setReviewChangeUndo(null);
    } catch (err) {
      alert('Rückgängig fehlgeschlagen: ' + (err.response?.data?.error || err.message));
    }
  }, [reviewChangeUndo, setReviewChangeRequired]);

  // Lokales Update der Orders (Optimistic UI)
  const updateLocalStatus = useCallback((id, status) => {
    const nid = Number(id);
    setAllOrders(prev => prev.map(o => (Number(o.id) === nid ? { ...o, status } : o)));
  }, []);

  const submitBulkStatus = useCallback(async (ids, status) => {
    if (!ids?.length) return;

    setSubmitting(true);
    try {
      await API.patch('/orders/bulk-update', { ids, fields: { status } });

      window.dispatchEvent(new CustomEvent('orders-updated', {
        detail: { source: 'reviews', ts: Date.now() },
      }));
    } catch (err) {
      console.error('bulk-update error:', err?.response?.status, err?.response?.data, err);

      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        err?.message ||
        String(err);

      alert('Fehler: ' + msg);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const toggleOrderMain = (id) => setSelectedMain(prev => ({ ...prev, [id]: !prev[id] }));

  const selectedCountMain = useMemo(
    () => Object.values(selectedMain).filter(Boolean).length, [selectedMain]
  );

  const reviewChangeCount = useMemo(
    () => allOrders.filter(o => o.review_change_required).length,
    [allOrders]
  );

  const visibleOrders = useMemo(() => {
    return allOrders.filter(o => {
      if (!o.review_change_required && selectedStatuses.length > 0 && !selectedStatuses.includes(o.status)) return false;
      if (selectedAccounts.length > 0 && !selectedAccounts.includes(o.account_name)) return false;
      if (showOnlyWithReview && !o.review_url) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const name = (o.product_name || '').toLowerCase();
        const num = (o.order_number || '').toLowerCase();
        const asin = (o.asin || '').toLowerCase();
        if (!name.includes(q) && !num.includes(q) && !asin.includes(q)) return false;
      }
      if (dateFrom && o.order_date && o.order_date < dateFrom) return false;
      if (dateTo && o.order_date && o.order_date > dateTo + 'T23:59:59') return false;
      return true;
    });
  }, [allOrders, selectedStatuses, selectedAccounts, showOnlyWithReview, searchTerm, dateFrom, dateTo]);

  const submitSelectedMain = async (status) => {
    const ids = Object.entries(selectedMain).filter(([, v]) => v).map(([id]) => parseInt(id, 10));
    if (!ids.length) return;
    await submitBulkStatus(ids, status);
    setSelectedMain({});
  };

  const sortedOrders = useMemo(() => {
    const flagFirst = (a, b) =>
      Number(!!b.review_change_required) - Number(!!a.review_change_required);

    if (agentSort) {
      const agentKey = o =>
        String(o.agent_name || 'zzz').trim().toLowerCase();
      const asinKey = o =>
        String(o.asin || '').trim().toUpperCase() ||
        `__ohne_asin_${o.id}`;
      const groupKey = o =>
        JSON.stringify([agentKey(o), asinKey(o)]);

      // Ältestes Bestelldatum je Agent/ASIN, unabhängig vom Account.
      const firstDate = new Map();
      for (const order of visibleOrders) {
        const key = groupKey(order);
        const date = toDateMs(order.order_date);
        const previous = firstDate.get(key);
        if (previous === undefined || date < previous) {
          firstDate.set(key, date);
        }
      }

      return [...visibleOrders].sort((a, b) => {
        const agentDiff = agentKey(a).localeCompare(agentKey(b));
        if (agentDiff !== 0) return agentDiff;

        const dateA = firstDate.get(groupKey(a));
        const dateB = firstDate.get(groupKey(b));
        if (dateA !== dateB) return dateA - dateB;

        const asinDiff = asinKey(a).localeCompare(asinKey(b));
        if (asinDiff !== 0) return asinDiff;

        const flagged = flagFirst(a, b);
        if (flagged !== 0) return flagged;

        return toDateMs(a.order_date) - toDateMs(b.order_date);
      });
    }

    // Ohne Agent-Sortierung bleibt die bisherige Logik unverändert.
    return [...visibleOrders].sort((a, b) => {
      const flagged = flagFirst(a, b);
      if (flagged !== 0) return flagged;
      return toDateMs(a.order_date) - toDateMs(b.order_date);
    });
  }, [visibleOrders, agentSort]);

  const navOrders = useMemo(() => {
    return allOrders
      .filter(o =>
        (selectedAccounts.length === 0 || selectedAccounts.includes(o.account_name)) &&
        (selectedStatuses.length === 0 || selectedStatuses.includes(o.status))
      )
      .sort((a, b) => toDateMs(a.order_date) - toDateMs(b.order_date));
  }, [allOrders, selectedAccounts, selectedStatuses]);

  const countsByAccount = useMemo(() => {
    const base = allOrders.filter(o => selectedStatuses.includes(o.status));
    return Object.fromEntries(
      accountList.map(acc => [acc, base.filter(o => o.account_name === acc).length])
    );
  }, [allOrders, selectedStatuses, accountList]);

  // ── KI Modal ──
  const openAiModal = useCallback(async (order) => {
    setAiModal({ order });
    setAiExtraInfo('');
    setAiResult(null);
    setAiError('');
    setAiCopied(false);
    setAiExisting(null);
    setAiLoading(true);

    const asin = getAsin(order);
    const key = `${asin || 'noasin'}|${order.account_name || 'noacc'}`;

    if (existingReviewCache.current.has(key)) {
      setAiExisting(existingReviewCache.current.get(key));
      setAiLoading(false);
      return;
    }

    try {
      const res = await API.get('/reviews', {
        params: { asin, account: order.account_name, limit: 1 },
      });
      const found = (res.data.reviews || [])[0] || null;
      existingReviewCache.current.set(key, found);
      setAiExisting(found);
    } catch {
      existingReviewCache.current.set(key, null);
    } finally {
      setAiLoading(false);
    }
  }, []);

  const closeAiModal = () => {
    setAiModal(null);
    setAiResult(null);
    setAiError('');
    setAiExisting(null);
  };

  const generateAiReview = async () => {
    if (!aiModal) return;
    setAiGenerating(true);
    setAiError('');
    setAiResult(null);
    try {
      const res = await API.post('/reviews/generate', {
        order_id: aiModal.order.id,
        extra_info: aiExtraInfo.trim() || undefined,
      });
      if (res.data.ok) {
        setAiResult({
          ...res.data.review,
          ai_model: res.data.ai_model || null,
          persona_name: res.data.persona_name || null,
          writing_profile: res.data.writing_profile || null,
        });
        setAiExisting(null);
        existingReviewCache.current = new Map();
      } else if (res.data.status === 'waiting_product_info') {
        const waitingReview = {
          status: 'waiting_product_info',
          message: res.data.message || 'Produktinformationen werden erneut geprüft.',
          product_resolution_status: res.data.productResolution?.status || 'waiting_product_info',
          product_resolution_next_check_at: res.data.productResolution?.next_check_at || null,
          product_resolution_error: res.data.productResolution?.last_error || null,
        };
        setAiExisting(waitingReview);
        setAiError('');
        existingReviewCache.current = new Map();
      } else if (res.data.status === 'needs_info') {
        const needsInfoReview = {
          status: 'needs_info',
          message: res.data.message || 'Produktinformationen nicht eindeutig. Bitte genauere Infos angeben.',
          product_resolution_error: res.data.productResolution?.last_error || null,
        };
        setAiExisting(needsInfoReview);
        setAiError(needsInfoReview.message);
        existingReviewCache.current = new Map();
      } else {
        setAiError(res.data.message || 'Fehler bei der Generierung.');
      }
    } catch (err) {
      const responseData = err?.response?.data || {};
      if (responseData.status === 'waiting_product_info' || responseData.status === 'needs_info') {
        const stateReview = {
          status: responseData.status,
          message: responseData.message || responseData.error || 'Produktinformationen nicht eindeutig.',
        };
        setAiExisting(stateReview);
        setAiError(responseData.status === 'needs_info' ? stateReview.message : '');
        existingReviewCache.current = new Map();
      } else {
        setAiError(responseData.error || responseData.message || err.message);
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const retryProductResolution = async () => {
    const asin = getAsin(aiModal?.order);
    if (!asin) return;

    setAiGenerating(true);
    setAiError('');
    try {
      await API.post(`/reviews/product-resolution/${asin}/retry`);
      existingReviewCache.current = new Map();
      setAiExisting(null);
      await generateAiReview();
    } catch (err) {
      setAiError(err?.response?.data?.error || err.message || 'Produktprüfung konnte nicht neu gestartet werden.');
    } finally {
      setAiGenerating(false);
    }
  };

  const copyAiReview = async (rev) => {
    const r = rev || aiResult;
    if (!r) return;
    await navigator.clipboard.writeText(r.text + (r.title ? '\n\n' + r.title : ''));
    setAiCopied(true);
    setTimeout(() => setAiCopied(false), 2000);
  };

  const openRegenerateConfirm = () => {
    if (!aiModal?.order) return;
    const asin = getAsin(aiModal.order);
    setRegenBulk(!!asin);
    setRegenInfo((aiExtraInfo || '').trim());
    setRegenConfirmOpen(true);
  };

  const cancelRegenerate = () => {
    setRegenConfirmOpen(false);
    setRegenRunning(false);
  };

  const runRegenerate = async () => {
    if (!aiModal?.order) return;

    const currentOrder = aiModal.order;
    const asin = getAsin(currentOrder);
    const info = (regenInfo || '').trim();
    const doBulk = regenBulk && !!asin;

    setRegenRunning(true);
    setAiError('');

    try {
      if (doBulk) {
        const res = await API.post('/reviews/regenerate-asin', {
          asin,
          extra_info: info || undefined,
        });

        const data = res.data || {};
        const reviews = Array.isArray(data.reviews) ? data.reviews : [];
        const currentReview = reviews.find(r => Number(r.order_id) === Number(currentOrder.id));
        const generatedReview = currentReview?.status === 'generated'
          ? currentReview
          : reviews.find(r => r.status === 'generated');

        existingReviewCache.current = new Map();

        if (currentReview?.status === 'generated') {
          setAiExisting(null);
          setAiResult({
            text: currentReview.review_text,
            title: currentReview.review_title,
            review_text: currentReview.review_text,
            review_title: currentReview.review_title,
            ai_model: currentReview.ai_model || null,
            persona_name: currentReview.persona_name || null,
          });
        } else if (currentReview?.status === 'skipped_existing_generated') {
          setAiExisting({
            status: 'generated',
            review_text: currentReview.review_text,
            review_title: currentReview.review_title,
          });
          setAiResult(null);
        } else if (generatedReview) {
          setAiExisting(null);
          setAiResult({
            text: generatedReview.review_text,
            title: generatedReview.review_title,
            review_text: generatedReview.review_text,
            review_title: generatedReview.review_title,
            ai_model: generatedReview.ai_model || null,
            persona_name: generatedReview.persona_name || null,
          });
        } else {
          setAiExisting(null);
          setAiResult(null);
        }

        const summary = data.message || `${data.generated || 0} generiert, ${data.skipped || 0} übersprungen, ${data.waiting_product_info || 0} warten auf Produktinfos, ${data.needs_info || 0} brauchen Infos, ${data.errors || 0} Fehler.`;
        const details = reviews
          .filter(r => r.status !== 'generated')
          .map(r => `${r.account_name || 'Account ?'}: ${r.status}${r.reason ? ` (${r.reason})` : ''}${r.error ? ` - ${r.error}` : ''}`)
          .join('\n');

        if ((data.errors || 0) > 0 || (data.needs_info || 0) > 0 || (data.waiting_product_info || 0) > 0 || details) {
          setAiError(details ? `${summary}\n${details}` : summary);
        }

        await fetchOrders();
      } else {
        setAiExisting(null);
        setAiResult(null);
        setAiExtraInfo(info);
        await generateAiReview();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        err?.message ||
        String(err);
      setAiError(msg);
      alert('Fehler: ' + msg);
    } finally {
      setRegenRunning(false);
      setRegenConfirmOpen(false);
    }
  };

  // ── KI Modal Navigation / Status Buttons ──
  const getNextOrder = useCallback((currentId) => {
    const idx = navOrders.findIndex(o => Number(o.id) === Number(currentId));
    if (idx < 0) return null;
    return navOrders[idx + 1] || null;
  }, [navOrders]);

  const openNextOrder = useCallback(() => {
    if (!aiModal?.order?.id) return;
    const next = getNextOrder(aiModal.order.id);
    if (!next) {
      alert('Kein weiterer Artikel in dieser gefilterten Liste.');
      return;
    }
    openAiModal(next);
  }, [aiModal, getNextOrder, openAiModal]);

  const markStatus = useCallback(async (status, goNext = false) => {
    if (!aiModal?.order?.id) return;
    if (submitting) return;

    const now = Date.now();
    if (now - lastActionAtRef.current < 350) return;
    lastActionAtRef.current = now;

    const currentId = Number(aiModal.order.id);
    const prevStatus = aiModal.order.status;
    const next = goNext ? getNextOrder(currentId) : null;

    updateLocalStatus(currentId, status);

    if (goNext) {
      if (next) openAiModal(next);
      else alert('Kein weiterer Artikel in dieser gefilterten Liste.');
    }

    try {
      await submitBulkStatus([currentId], status);
    } catch (err) {
      updateLocalStatus(currentId, prevStatus);
    }
  }, [aiModal, submitting, getNextOrder, openAiModal, submitBulkStatus, updateLocalStatus]);

  const markReviewed = useCallback(async (goNext = false) => {
    await markStatus('Reviewed', goNext);
  }, [markStatus]);

  const markStorno = useCallback(async () => {
    await markStatus('Storno', false);
  }, [markStatus]);

  // ── Group Modal ──
  const openGroupModal = (order) => {
    const asin = getAsin(order);
    const name = getProductName(order);
    setSelectedGroup({});
    setGroupStatusToSet('Eingereicht');
    if (asin) {
      setGroupMode('asin'); setGroupAsin(asin); setGroupNameRaw(''); setGroupNameKey('');
      setGroupTitle(name || asin); setGroupRange({ from: null, to: null }); setGroupModalOpen(true);
      return;
    }
    const ref = order?.order_date ? new Date(order.order_date) : new Date();
    const from = new Date(ref); from.setDate(from.getDate() - 10);
    const to = new Date(ref); to.setDate(to.getDate() + 10);
    setGroupMode('name'); setGroupAsin(null); setGroupNameRaw(name); setGroupNameKey(normalizeName(name));
    setGroupTitle(name || 'Ohne ASIN'); setGroupRange({ from, to }); setGroupModalOpen(true);
  };

  const closeGroupModal = () => {
    setGroupModalOpen(false); setGroupMode('asin'); setGroupAsin(null);
    setGroupNameRaw(''); setGroupNameKey(''); setGroupTitle('');
    setGroupRange({ from: null, to: null }); setSelectedGroup({});
  };

  const groupOrders = useMemo(() => {
    if (!groupModalOpen) return [];
    const base = allOrders.filter(o => selectedAccounts.length === 0 || selectedAccounts.includes(o.account_name));
    if (groupMode === 'asin') return base.filter(o => getAsin(o) === groupAsin);

    const fromMs = groupRange?.from ? groupRange.from.getTime() : -Infinity;
    const toMs = groupRange?.to ? groupRange.to.getTime() : Infinity;

    return base.filter(o => {
      if (getAsin(o)) return false;
      if (normalizeName(getProductName(o)) !== groupNameKey) return false;
      const t = toDateMs(o.order_date);
      return t >= fromMs && t <= toMs;
    });
  }, [groupModalOpen, groupMode, groupAsin, groupNameKey, groupRange, allOrders, selectedAccounts]);

  const toggleOrderGroup = (id) => setSelectedGroup(prev => ({ ...prev, [id]: !prev[id] }));
  const selectedCountGroup = useMemo(() => Object.values(selectedGroup).filter(Boolean).length, [selectedGroup]);

  const selectAllGroup = () => {
    const next = {};
    groupOrders.forEach(o => {
      if (!isLockedStatus(o.status)) next[o.id] = true;
    });
    setSelectedGroup(next);
  };

  const submitSelectedGroup = async () => {
    const ids = Object.entries(selectedGroup).filter(([, v]) => v).map(([id]) => parseInt(id, 10));
    if (!ids.length) return;
    await submitBulkStatus(ids, groupStatusToSet);
    setSelectedGroup({});
  };

  const copyAllGroupLinks = async () => {
    const lines = groupOrders.filter(o => o.review_url)
      .map(o => `${o.account_name || '-'} | ${o.order_number || '-'} | ${formatDate(o.order_date)} | ${o.review_url}`);
    if (!lines.length) { alert('Keine Review-Links in dieser Gruppe.'); return; }
    await copyText(lines.join('\n'));
  };

  const copySelectedGroupLinks = async () => {
    const lines = groupOrders.filter(o => selectedGroup[o.id] && o.review_url)
      .map(o => `${o.account_name || '-'} | ${o.order_number || '-'} | ${formatDate(o.order_date)} | ${o.review_url}`);
    if (!lines.length) { alert('Keine ausgewählten Review-Links.'); return; }
    await copyText(lines.join('\n'));
  };

  const copyGroupTelegram = async () => {
    const orders = groupOrders;
    if (!orders.length) { alert('Keine Bestellungen in dieser Gruppe.'); return; }

    const asin = getAsin(orders[0]);
    const productName = getProductName(orders[0]);
    const amazonLink = asin ? `https://www.amazon.de/dp/${asin}` : '';
    const date = formatDate(orders[0].order_date);

    let text = `📅 Bestelldatum: ${date}\n`;
    text += `📦 ${productName}\n`;
    if (amazonLink) text += `${amazonLink}\n`;
    text += `\n`;

    orders.forEach(o => {
      text += `🔢 ${o.order_number || '-'}\n`;
      text += o.review_url ? `🔗 ${o.review_url}\n` : `🔗 (kein Review Link)\n`;
      text += `\n`;
    });

    await copyText(text.trim());
  };

  const tableRows = useMemo(() => {
    const rows = [];
    if (agentSort) {
      let lastAgent = null;
      for (const order of sortedOrders) {
        const agent = order.agent_name || '(kein Agent)';
        if (agent !== lastAgent) {
          rows.push({ type: 'agent', agent, orders: sortedOrders.filter(o => (o.agent_name || '(kein Agent)') === agent) });
          lastAgent = agent;
        }
        rows.push({ type: 'order', order });
      }
    } else {
      let lastDate = null;
      for (const order of sortedOrders) {
        const dateKey = formatDate(order.order_date);
        if (dateKey !== lastDate) {
          rows.push({ type: 'date', date: dateKey, orders: sortedOrders.filter(o => formatDate(o.order_date) === dateKey) });
          lastDate = dateKey;
        }
        rows.push({ type: 'order', order });
      }
    }
    return rows;
  }, [sortedOrders, agentSort]);

  const KiButton = ({ order }) => {
    const rev = allAiReviews[order.id];
    if (rev?.status === 'generated') return (
      <button className="btn btn-ghost" onClick={() => openAiModal(order)} title="Review vorhanden" style={{ padding: '2px 6px', fontSize: 13 }}><CheckCircle size={15}/></button>
    );
    if (rev?.status === 'waiting_product_info') return (
      <button className="btn btn-ghost" onClick={() => openAiModal(order)} title="Wartet auf bessere Produktinfos" style={{ padding: '2px 6px', fontSize: 13 }}><Clock3 size={15}/></button>
    );
    if (rev?.status === 'needs_info') return (
      <button className="btn btn-ghost" onClick={() => openAiModal(order)} title="Mehr Infos benötigt" style={{ padding: '2px 6px', fontSize: 13 }}><AlertCircle size={15}/></button>
    );
    return (
      <button className="btn btn-ghost" onClick={() => openAiModal(order)} title="KI Review anzeigen/generieren" style={{ padding: '2px 6px', fontSize: 13 }}><Sparkles size={15}/></button>
    );
  };

  const saveNote = async () => {
    if (!noteModal) return;
    setNoteSaving(true);
    try {
      if (noteModal.asin) {
        await API.put(`/asin-notes/${noteModal.asin}`, { description: noteText });
      }
      setAllOrders(prev => prev.map(o => (o.asin && o.asin === noteModal.asin) ? { ...o, asin_description: noteText } : o));
      setNoteModal(null);
    } finally {
      setNoteSaving(false);
    }
  };

  return (
    <div className="page reviews-v2">
      <style>{`
        .modalOverlay { position:fixed; inset:0; background:rgba(0,0,0,0.55); display:flex; align-items:center; justify-content:center; padding:18px; z-index:9999; }
        .modalCard { width:min(980px,96vw); max-height:min(82vh,820px); overflow:hidden; border-radius:14px; background:var(--bg2); border:1px solid var(--border); box-shadow:0 25px 60px rgba(0,0,0,0.35); display:flex; flex-direction:column; }
        .modalHeader { padding:14px 16px; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--border); }
        .modalTitle { font-family:var(--rv-font); font-weight:800; font-size:14px; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:560px; }
        .modalSub { font-family:ui-monospace,Consolas,monospace; font-size:11px; color:var(--text3); display:inline-flex; align-items:center; gap:8px; margin-top:2px; }
        .modalBody { padding:12px 14px; overflow:auto; }
        .modalFooter { padding:12px 14px; border-top:1px solid var(--border); display:flex; gap:10px; justify-content:space-between; align-items:center; flex-wrap:wrap; background:rgba(255,255,255,0.02); }
        .groupRow { display:flex; align-items:center; gap:12px; padding:10px; border:1px solid var(--border); border-radius:10px; background:rgba(255,255,255,0.02); margin-bottom:10px; }
        .groupRow.dim { opacity:0.45; }
        .pill { border-radius:999px; padding:2px 8px; font-size:10px; font-weight:700; font-family:var(--rv-font); border:1px solid var(--border); color:var(--text3); background:rgba(255,255,255,0.03); }
        tr.date-row td { background:var(--bg3); padding:5px 10px; border-bottom:1px solid var(--border); }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Reviews</h1><p className="rv-subtitle">Rezensionen verwalten, Änderungen prüfen und den Versand vorbereiten</p>
          <p style={{ color: 'var(--text2)', marginTop: 4, fontSize: 13 }}>
            {visibleOrders.length} Bestellungen
            {reviewChangeCount > 0 && <span style={{ color: '#fb923c', fontWeight: 700 }}> · ⚠ {reviewChangeCount} Review-Änderung{reviewChangeCount === 1 ? '' : 'en'}</span>}
            {selectedCountMain > 0 && <span style={{ color: 'var(--accent3)', fontWeight: 600 }}> · {selectedCountMain} ausgewählt</span>}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
          {selectedCountMain > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {STATUS_OPTIONS.map(status => {
                const badge = STATUS_BADGE[status];
                return (
                  <button
                    key={status}
                    className={`btn rv-status-action status-${status}`}
                    onClick={() => submitSelectedMain(status)}
                    disabled={submitting || regenConfirmOpen}
                    style={{
                      background: badge.bg,
                      color: badge.color,
                      border: `1px solid ${badge.color}`,
                      padding: '6px 12px',
                      fontSize: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Send size={13} />
                    {submitting ? 'Wird gesetzt...' : `${selectedCountMain} → ${badge.label}`}
                  </button>
                );
              })}
            </div>
          )}

          <button className="btn btn-ghost" onClick={async () => {
            setImportingReviews(true);
            try {
              const res = await API.post('/mail/import-reviews');
              alert(`✅ ${res.data.updated} Reviews importiert, ${res.data.skipped} übersprungen`);
              fetchOrders();
            } catch(e) {
              alert('Fehler: ' + (e.response?.data?.error || e.message));
            } finally { setImportingReviews(false); }
          }} disabled={importingReviews} title="Reviews aus Gmail importieren">
            <Mail size={16}/><span>{importingReviews?'Import läuft …':'Gmail-Reviews importieren'}</span>
          </button>

          <button
            className="btn btn-ghost"
            onClick={() => setAgentSort(s => !s)}
            style={{
              background: agentSort ? 'rgba(99,102,241,0.15)' : 'transparent',
              border: agentSort ? '1px solid var(--accent)' : '1px solid var(--border)',
              color: agentSort ? 'var(--accent)' : 'var(--text2)',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            Nach Agent
            {agentSort && <span style={{ background: 'var(--accent)', color: 'white', borderRadius: 10, fontSize: 10, padding: '1px 6px' }}>AN</span>}
          </button>

          <button className="btn btn-ghost" onClick={fetchOrders} aria-label="Reviews aktualisieren"><RefreshCw size={14} /></button>
        </div>
      </div>

      {pageError&&<div className="rv-feedback error" role="alert"><AlertCircle size={18}/>{pageError}<button className="rv-button" onClick={fetchOrders}>Erneut laden</button></div>}
      <div className="rv-global-stats" aria-label="Review-Übersicht">
        <div><span className="rv-stat-icon"><Star size={19}/></span><strong>{allOrders.length}</strong><small>Review-Positionen</small></div>
        <div><span className="rv-stat-icon green"><CheckCircle size={19}/></span><strong>{openReviewCount ?? '–'}</strong><small>Offen mit Reviewlink</small></div>
        <div><span className="rv-stat-icon amber"><AlertCircle size={19}/></span><strong>{reviewChangeCount}</strong><small>Änderungen offen</small></div>
        <div><span className="rv-stat-icon"><MessageCircle size={19}/></span><strong>{selectedCountMain}</strong><small>Zur Statusänderung markiert</small></div>
      </div>
      <div className="rv-workspace-tabs" role="tablist" aria-label="Review-Bereiche">
        <button role="tab" id="reviews-tab" aria-controls="reviews-content" aria-selected={activeView==='reviews'} onClick={()=>setActiveView('reviews')}><Star size={16}/>Review-Verwaltung</button>
        <button role="tab" id="dispatch-tab" aria-controls="dispatch-content" aria-selected={activeView==='dispatch'} onClick={()=>setActiveView('dispatch')}><Send size={16}/>Versandübersicht</button>
      </div>
      <div id="dispatch-content" role="tabpanel" aria-labelledby="dispatch-tab" hidden={activeView!=='dispatch'}><ReviewDispatchPanel knownOrders={allOrders} request={dispatchRequest} onSummaryChange={setOpenReviewCount}/></div>
      <div id="reviews-content" role="tabpanel" aria-labelledby="reviews-tab" hidden={activeView!=='reviews'}>
      <div className="rv-selection-bar"><span>Die Tabellen-Checkboxen dienen weiterhin der <strong>Statusverwaltung</strong>.</span>
        <button className="rv-button" disabled={!selectedCountMain||loading||!!pageError} onClick={()=>requestDispatch(Object.keys(selectedMain).filter(id=>selectedMain[id]).map(Number))}><Send size={15}/>Auswahl für Versand prüfen</button>
        <button className="rv-button primary" disabled={loading||!!pageError} onClick={()=>requestDispatch([],false,true)}><MessageCircle size={15}/>Alle regulären Reviews für Versand prüfen</button>
      </div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Suche nach Produkt, Bestellnummer, ASIN..."
              style={{ width: '100%', paddingLeft: 30, paddingRight: 10, paddingTop: 6, paddingBottom: 6, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="Von"
            style={{ padding: '6px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text2)', fontSize: 12, outline: 'none' }} />
          <span style={{ color: 'var(--text3)', fontSize: 11 }}>–</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} title="Bis"
            style={{ padding: '6px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text2)', fontSize: 12, outline: 'none' }} />
          {(searchTerm || dateFrom || dateTo) && (
            <button className="btn" onClick={() => { setSearchTerm(''); setDateFrom(''); setDateTo(''); }}
              style={{ padding: '5px 10px', fontSize: 11, color: 'var(--text3)', border: '1px solid var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', gap: 4 }}>
              <X size={12} /> Reset
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--rv-font)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Status:</span>
            {STATUS_OPTIONS.map(s => {
              const b = STATUS_BADGE[s];
              const active = selectedStatuses.includes(s);
              return (
                <button key={s} onClick={() => setSelectedStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                  className={`btn rv-status-filter status-${s} ${active?'selected':''}`} aria-pressed={active} style={{ background: active ? b.bg : 'transparent', color: active ? b.color : 'var(--text3)', border: `1px solid ${active ? b.color : 'var(--border)'}`, padding: '4px 10px', fontSize: 11 }}>
                  {b.label}
                </button>
              );
            })}
          </div>

          <button className="btn" onClick={() => setShowOnlyWithReview(v => !v)}
            style={{ background: showOnlyWithReview ? 'rgba(74,222,128,0.15)' : 'transparent', color: showOnlyWithReview ? '#4ade80' : 'var(--text3)', border: `1px solid ${showOnlyWithReview ? '#4ade80' : 'var(--border)'}`, padding: '4px 10px', fontSize: 11 }}>
            <ExternalLink size={13}/>Nur mit Review-Link
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--rv-font)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Account:</span>
            <button className="btn" onClick={() => setSelectedAccounts([])}
              style={{ background: selectedAccounts.length === 0 ? 'var(--accent)' : 'transparent', color: selectedAccounts.length === 0 ? 'white' : 'var(--text2)', border: selectedAccounts.length === 0 ? 'none' : '1px solid var(--border)', padding: '4px 10px', fontSize: 11 }}>Alle</button>
            {accountList.map(acc => {
              const count = countsByAccount[acc] || 0;
              if (count === 0) return null;
              const active = selectedAccounts.includes(acc);
              return (
                <button key={acc} onClick={() => setSelectedAccounts(prev => prev.includes(acc) ? prev.filter(a => a !== acc) : [...prev, acc])}
                  className="btn" style={{ background: active ? 'var(--accent)' : 'transparent', color: active ? 'white' : 'var(--text2)', border: active ? 'none' : '1px solid var(--border)', padding: '4px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
                  {acc.replace('Account ', 'Acc ')}
                  <span style={{ background: active ? 'rgba(255,255,255,0.2)' : 'var(--bg3)', borderRadius: 10, padding: '0 5px', fontSize: 10 }}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
      ) : visibleOrders.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <CheckCircle size={32} style={{ color: 'var(--accent2)', marginBottom: 12 }} />
          <p style={{ color: 'var(--text2)', fontFamily: 'var(--rv-font)', fontWeight: 600 }}>Alles erledigt!</p>
        </div>
      ) : (
        <div className="card rv-table-scroll" style={{ padding: 0 }}>
          <table className="rv-review-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th style={{ width: 34 }}></th>
                <th>Datum</th>
                <th>Produkt</th>
                <th>Bestellnr.</th>
                <th>Agent</th>
                <th>Account</th>
                <th></th>
                <th>Review Link</th>
                <th style={{ width: 40 }}>KI</th>
                <th>Status</th><th>Versand</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, idx) => {
                if (row.type === 'agent') {
                  const eligible = row.orders.filter(o => !isLockedStatus(o.status));
                  const allSel = eligible.length > 0 && eligible.every(o => !!selectedMain[o.id]);
                  const anySel = eligible.some(o => !!selectedMain[o.id]);
                  return (
                    <tr key={`agent-${row.agent}-${idx}`} className="date-row">
                      <td>
                        <input type="checkbox" checked={allSel}
                          ref={el => { if (el) el.indeterminate = !allSel && anySel; }}
                          onChange={() => {
                            const next = { ...selectedMain };
                            eligible.forEach(o => { next[o.id] = !allSel; });
                            setSelectedMain(next);
                          }}
                          disabled={eligible.length === 0}
                          style={{ width: 'auto', cursor: eligible.length === 0 ? 'not-allowed' : 'pointer' }} />
                      </td>
                      <td colSpan={11}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 700, fontFamily: 'var(--rv-font)' }}>{row.agent}</span>
                          <span style={{ color: 'var(--text3)', fontSize: 11 }}>{row.orders.length} Bestellung(en)</span>
                        </div>
                      </td>
                    </tr>
                  );
                }

                if (row.type === 'date') {
                  const eligible = row.orders.filter(o => !isLockedStatus(o.status));
                  const allSel = eligible.length > 0 && eligible.every(o => !!selectedMain[o.id]);
                  const anySel = eligible.some(o => !!selectedMain[o.id]);
                  return (
                    <tr key={`date-${row.date}-${idx}`} className="date-row">
                      <td>
                        <input type="checkbox" checked={allSel}
                          ref={el => { if (el) el.indeterminate = !allSel && anySel; }}
                          onChange={() => {
                            const next = { ...selectedMain };
                            eligible.forEach(o => { next[o.id] = !allSel; });
                            setSelectedMain(next);
                          }}
                          disabled={eligible.length === 0}
                          style={{ width: 'auto', cursor: eligible.length === 0 ? 'not-allowed' : 'pointer' }} />
                      </td>
                      <td colSpan={11}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ color: 'var(--text3)', fontSize: 11 }}>{row.orders.length} Bestellung(en)</span>
                        </div>
                      </td>
                    </tr>
                  );
                }

                const { order } = row;
                const b = STATUS_BADGE[order.status] || STATUS_BADGE.Bestellt;
                const disabled = isLockedStatus(order.status);
                const asin = getAsin(order);

                return (
                  <tr key={order.id} style={{
                    opacity: disabled && !order.review_change_required ? 0.45 : 1,
                    background: order.review_change_required ? 'rgba(249,115,22,0.07)' : undefined,
                    boxShadow: order.review_change_required ? 'inset 3px 0 0 #f97316' : undefined,
                  }}>
                    <td>
                      <input type="checkbox" aria-label={`Statusauswahl ${order.order_number || order.id}`} checked={!!selectedMain[order.id]} onChange={() => toggleOrderMain(order.id)}
                        style={{ width: 'auto', cursor: disabled ? 'not-allowed' : 'pointer' }} disabled={disabled} />
                    </td>
                    <td>
                      <button className="btn btn-ghost" onClick={() => openGroupModal(order)}
                        title={asin ? 'ASIN-Gruppe öffnen' : 'Name-Matching ±10 Tage'} style={{ padding: '2px 6px', cursor: 'pointer' }}>
                        <Search size={14} />
                      </button>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', color: 'var(--text3)', fontSize: 12 }}>{formatDate(order.order_date)}</td>
                    <td style={{ maxWidth: 280 }}>
                      {order.review_change_required && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, minWidth: 0 }}>
                          <span
                            title={order.review_change_note || 'Review muss geändert werden'}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              background: 'rgba(249,115,22,0.14)', color: '#fb923c',
                              border: '1px solid rgba(249,115,22,0.4)', borderRadius: 6,
                              padding: '2px 6px', fontSize: 9, fontWeight: 800, fontFamily: 'var(--rv-font)',
                            }}
                          >
                            ⚠ REVIEW MUSS GEÄNDERT WERDEN
                          </span>
                          <button
                            type="button"
                            onClick={() => completeReviewChange(order)}
                            title="Änderung erledigt"
                            style={{
                              width: 22, height: 22, flexShrink: 0, borderRadius: 6,
                              border: '1px solid rgba(74,222,128,0.45)',
                              background: 'rgba(74,222,128,0.10)', color: '#4ade80',
                              cursor: 'pointer', fontWeight: 900, lineHeight: 1,
                            }}
                          >✓</button>
                        </div>
                      )}
                      {order.review_change_required && order.review_change_note && (
                        <div className="rv-change-note" style={{ color: '#fdba74', fontSize: 10, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={order.review_change_note}>
                          {order.review_change_note}
                        </div>
                      )}
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }} title={getProductName(order)}>
                        {asin ? (
                          <a href={`https://www.amazon.de/dp/${asin}`} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }} title={asin}>{getProductName(order)}</a>
                        ) : getProductName(order)}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'ui-monospace,Consolas,monospace', fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                      {order.order_number || '-'}
                      {order.order_number && (
                        <button onClick={() => { navigator.clipboard.writeText(order.order_number); setCopiedOrderId(order.id); }} title="Kopieren" style={{ marginLeft: 6, background: copiedOrderId === order.id ? 'rgba(74,222,128,0.15)' : 'none', border: 'none', cursor: 'pointer', color: copiedOrderId === order.id ? '#4ade80' : 'var(--text3)', fontSize: 11, padding: '0 2px', transition: 'all 0.2s' }}>{copiedOrderId === order.id ? '✓' : '⧉'}</button>
                      )}
                    </td>
                    <td style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}>{order.agent_name || '-'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{order.account_name || '-'}</td>
                    <td style={{ width: 32 }}>
                      <button onClick={() => { setNoteModal(order); setNoteText(order.asin_description || ''); }} title="Notiz"
                        style={{ background: order.asin_description ? 'rgba(251,191,36,0.15)' : 'none', border: 'none', cursor: 'pointer', color: order.asin_description ? '#fbbf24' : 'var(--text3)', fontSize: 14, padding: '2px 4px', borderRadius: 4 }}>
                        <StickyNote size={15}/>
                      </button>
                    </td>
                    <td>
                      {order.review_url ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <a href={order.review_url} target="_blank" rel="noreferrer"
                            style={{ color: 'var(--accent2)', textDecoration: 'none', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <ExternalLink size={12} />Review
                          </a>
                          <button onClick={() => copyLink(order.review_url, order.id)} className="btn btn-ghost" style={{ padding: '2px 6px' }}>
                            {copiedId === order.id ? '✓' : <Copy size={11} />}
                          </button>
                        </div>
                      ) : <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>}
                    </td>
                    <td><KiButton order={order} /></td>
                    <td>
                      <span className={`rv-order-status status-${order.status}`}>{b.label}</span>
                    </td>
                    <td><button className="rv-button" disabled={!order.review_url} onClick={()=>requestDispatch([order.id],!!order.review_change_required)} title={order.review_change_required?'Separate Prüfung; niemals Sammelversand':'Einzelversand vorbereiten'}><Send size={13}/>{order.review_change_required?'Manuell an WeChat senden':'Versenden'}</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      </div>
      {aiModal && (
        <div className="modalOverlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeAiModal(); }}>
          <div className="modalCard" style={{ width: 'min(600px,96vw)' }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <div>
                <div className="modalTitle"><Sparkles size={16}/> KI Review</div>
                <div className="modalSub">
                  <span className="pill">{aiModal.order.account_name}</span>
                  <span className="pill">{clampText(getProductName(aiModal.order), 60)}</span>
                </div>
              </div>
              <button className="btn btn-ghost" onClick={closeAiModal} style={{ padding: '6px 8px' }}><X size={16} /></button>
            </div>

            <div className="modalBody">
              {aiLoading && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>⏳ Prüfe vorhandene Reviews...</div>
              )}

              {!aiLoading && aiExisting?.status === 'waiting_product_info' && !aiResult && (
                <>
                  <div style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)', borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 12, color: '#60a5fa' }}>
                    ⏳ <strong>Produktinfos werden erneut geprüft</strong>
                    <span style={{ color: 'var(--text2)', marginTop: 5, display: 'block', lineHeight: 1.5 }}>
                      {aiExisting.message || 'Der Produkttitel ist nicht eindeutig. Die ASIN wird zentral nur einmal geprüft und das Ergebnis gilt für alle Bestellungen dieser ASIN.'}
                    </span>
                    {aiExisting.product_resolution_next_check_at && (
                      <span style={{ color: 'var(--text3)', marginTop: 5, display: 'block' }}>
                        Nächster automatischer Versuch: {formatDate(aiExisting.product_resolution_next_check_at)}
                      </span>
                    )}
                    {aiExisting.product_resolution_error && (
                      <span style={{ color: 'var(--text3)', marginTop: 5, display: 'block' }}>
                        Letzte Prüfung: {aiExisting.product_resolution_error}
                      </span>
                    )}
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'var(--rv-font)', textTransform: 'uppercase' }}>Zusatzinfos (optional)</label>
                    <textarea
                      value={aiExtraInfo}
                      onChange={e => setAiExtraInfo(e.target.value)}
                      placeholder="Wenn du sicher weißt was das Produkt ist, kannst du die automatische Wartezeit mit eigenen Infos übergehen."
                      rows={3}
                      style={{ resize: 'vertical', fontSize: 12, width: '100%' }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-ghost" onClick={retryProductResolution} disabled={aiGenerating}>
                      {aiGenerating ? '⏳ Prüfe...' : '🔎 Jetzt erneut prüfen'}
                    </button>
                    <button className="btn btn-primary" onClick={generateAiReview} disabled={aiGenerating || !aiExtraInfo.trim()}>
                      {aiGenerating ? '⏳ Generiere...' : '🤖 Mit eigenen Infos generieren'}
                    </button>
                  </div>
                </>
              )}

              {!aiLoading && aiExisting?.status === 'needs_info' && !aiResult && (
                <>
                  <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 12, color: '#fbbf24' }}>
                    ⚠️ <strong>Produktinformationen nicht eindeutig</strong>
                    <span style={{ color: 'var(--text3)', marginTop: 4, display: 'block' }}>
                      Die ASIN wurde automatisch geprüft, konnte aber nicht sicher aufgelöst werden. Füge bitte kurz Produktart, Nutzung oder wichtige Merkmale hinzu.
                    </span>
                    {aiExisting.product_resolution_error && (
                      <span style={{ color: 'var(--text3)', marginTop: 4, display: 'block' }}>Letzte Prüfung: {aiExisting.product_resolution_error}</span>
                    )}
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'var(--rv-font)', textTransform: 'uppercase' }}>Zusatzinfos</label>
                    <textarea
                      value={aiExtraInfo}
                      onChange={e => setAiExtraInfo(e.target.value)}
                      placeholder="z.B. 'LED Deckenlampe für Wohnzimmer, warmweißes Licht, dimmbar'"
                      rows={3}
                      style={{ resize: 'vertical', fontSize: 12, width: '100%' }}
                    />
                  </div>
                  {aiError && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#ef4444' }}>❌ {aiError}</div>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button className="btn btn-ghost" onClick={closeAiModal}>Abbrechen</button>
                    <button className="btn btn-primary" onClick={generateAiReview} disabled={aiGenerating || !aiExtraInfo.trim()}>
                      {aiGenerating ? '⏳ Generiere...' : '🤖 Mit Zusatzinfos generieren'}
                    </button>
                  </div>
                </>
              )}

              {!aiLoading && aiExisting?.status === 'generated' && !aiResult && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--rv-font)', textTransform: 'uppercase', marginBottom: 6 }}>Titel</div>
                    <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, color: 'var(--accent2)' }}>{aiExisting.review_title || '—'}</div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--rv-font)', textTransform: 'uppercase', marginBottom: 6 }}>Review Text</div>
                    <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '12px 14px', fontSize: 13, lineHeight: 1.6, color: 'var(--text)', whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto' }}>
                      {aiExisting.review_text}
                    </div>
                  </div>
                  {(aiExisting.persona_name || aiExisting.ai_model || aiExisting.resolved_product_type) && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
                      {aiExisting.persona_name && <span className="pill">Persona: {aiExisting.persona_name}</span>}
                      {aiExisting.ai_model && <span className="pill">Modell: {aiExisting.ai_model}</span>}
                      {aiExisting.resolved_product_type && <span className="pill">Produkt: {aiExisting.resolved_product_type}</span>}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button className="btn btn-ghost" onClick={openRegenerateConfirm}>🔄 Neu generieren</button>
                    <button className="btn btn-primary" onClick={() => copyAiReview({ text: aiExisting.review_text, title: aiExisting.review_title })}>
                      {aiCopied ? '✓ Kopiert!' : <><Copy size={13} /> Kopieren</>}
                    </button>
                  </div>
                </>
              )}

              {!aiLoading && aiResult && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--rv-font)', textTransform: 'uppercase', marginBottom: 6 }}>Titel</div>
                    <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, color: 'var(--accent2)' }}>{aiResult.title || '—'}</div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--rv-font)', textTransform: 'uppercase', marginBottom: 6 }}>Review Text</div>
                    <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '12px 14px', fontSize: 13, lineHeight: 1.6, color: 'var(--text)', whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto' }}>
                      {aiResult.text}
                    </div>
                  </div>
                  {(aiResult.persona_name || aiResult.ai_model) && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
                      {aiResult.persona_name && <span className="pill">Persona: {aiResult.persona_name}</span>}
                      {aiResult.ai_model && <span className="pill">Modell: {aiResult.ai_model}</span>}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button className="btn btn-ghost" onClick={openRegenerateConfirm}>🔄 Neu generieren</button>
                    <button className="btn btn-primary" onClick={() => copyAiReview(aiResult)}>
                      {aiCopied ? '✓ Kopiert!' : <><Copy size={13} /> Kopieren</>}
                    </button>
                  </div>
                </>
              )}

              {!aiLoading && !aiExisting && !aiResult && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'var(--rv-font)', textTransform: 'uppercase' }}>Zusatzinfos (optional)</label>
                    <textarea
                      value={aiExtraInfo}
                      onChange={e => setAiExtraInfo(e.target.value)}
                      placeholder="Produktart, Material, Besonderheiten... (leer lassen = nur Titel verwenden)"
                      rows={4}
                      style={{ resize: 'vertical', fontSize: 12, width: '100%' }}
                    />
                  </div>
                  {aiError && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#ef4444' }}>❌ {aiError}</div>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button className="btn btn-ghost" onClick={closeAiModal}>Abbrechen</button>
                    <button className="btn btn-primary" onClick={generateAiReview} disabled={aiGenerating}>
                      {aiGenerating ? '⏳ Generiere...' : '🤖 Jetzt generieren'}
                    </button>
                  </div>
                </>
              )}

              {!aiLoading && regenConfirmOpen && (
                <div style={{ marginTop: 14, padding: 12, borderRadius: 10, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.03)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontFamily: 'var(--rv-font)', fontWeight: 800, fontSize: 12, color: 'var(--text)' }}>
                      Neu generieren
                    </div>
                    <button className="btn btn-ghost" onClick={cancelRegenerate} style={{ padding: '4px 8px' }}>
                      <X size={14} />
                    </button>
                  </div>

                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
                    {getAsin(aiModal?.order)
                      ? 'Soll die komplette ASIN-Gruppe (alle Accounts) neu generiert werden?'
                      : 'Dieser Artikel hat keine ASIN – neu generieren gilt nur für den aktuellen Account.'}
                  </div>

                  {getAsin(aiModal?.order) && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                      <button
                        className="btn"
                        onClick={() => setRegenBulk(false)}
                        disabled={regenRunning}
                        style={{
                          border: !regenBulk ? '1px solid var(--accent2)' : '1px solid var(--border)',
                          background: !regenBulk ? 'rgba(59,130,246,0.15)' : 'transparent',
                          color: !regenBulk ? 'var(--accent2)' : 'var(--text2)'
                        }}
                      >
                        Nur dieser Account
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => setRegenBulk(true)}
                        disabled={regenRunning}
                        style={{ background: regenBulk ? 'var(--accent3)' : 'transparent', border: regenBulk ? 'none' : '1px solid var(--border)' }}
                      >
                        Ganze ASIN-Gruppe
                      </button>
                    </div>
                  )}

                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: 'block', marginBottom: 6, color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--rv-font)', textTransform: 'uppercase' }}>
                      Zusatzinfos (optional)
                    </label>
                    <textarea
                      value={regenInfo}
                      onChange={e => setRegenInfo(e.target.value)}
                      placeholder="z.B. 'LED Deckenlampe, warmweiß, dimmbar, Wohnzimmer' (hilft bei falschen/unspezifischen Reviews)"
                      rows={3}
                      style={{ resize: 'vertical', fontSize: 12, width: '100%' }}
                      disabled={regenRunning}
                    />
                  </div>

                  {aiError && (
                    <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 12, color: '#ef4444' }}>
                      ❌ {aiError}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                    <button className="btn btn-ghost" onClick={cancelRegenerate} disabled={regenRunning}>
                      Abbrechen
                    </button>
                    <button className="btn btn-primary" onClick={runRegenerate} disabled={regenRunning}>
                      {regenRunning ? '⏳ Läuft...' : '🔄 Neu generieren'}
                    </button>
                  </div>
                </div>
              )}

              {!aiLoading && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      className="btn"
                      onClick={() => markReviewed(false)}
                      disabled={submitting || regenConfirmOpen}
                      style={{ border: '1px solid var(--border)' }}
                    >
                      ✅ Status: Reviewed
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={() => markReviewed(true)}
                      disabled={submitting || regenConfirmOpen}
                      style={{ background: 'var(--accent3)', border: 'none' }}
                    >
                      ✅ Reviewed + Nächster
                    </button>

                    <button
                      className="btn"
                      onClick={markStorno}
                      disabled={submitting || regenConfirmOpen}
                      style={{
                        border: '1px solid #ef4444',
                        color: '#ef4444',
                        background: 'rgba(239,68,68,0.08)'
                      }}
                    >
                      ⛔ Storno
                    </button>

                    <button
                      className="btn"
                      onClick={() => markStatus('Privat', false)}
                      disabled={submitting || regenConfirmOpen}
                      style={{
                        border: '1px solid #3b82f6',
                        color: '#3b82f6',
                        background: 'rgba(59,130,246,0.08)'
                      }}
                    >
                      🔒 Privat
                    </button>
                  </div>

                  <button
                    className="btn"
                    onClick={openNextOrder}
                    disabled={submitting || regenConfirmOpen}
                    style={{ border: '1px solid var(--border)' }}
                  >
                    Nächster →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {groupModalOpen && (
        <div className="modalOverlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeGroupModal(); }}>
          <div className="modalCard" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <div>
                <div className="modalTitle">{groupTitle || groupAsin || 'Gruppe'}</div>
                <div className="modalSub">
                  <span className="pill">{groupOrders.length} Bestellung(en)</span>
                  {groupMode === 'asin' && groupAsin && (
                    <a href={`https://www.amazon.de/dp/${groupAsin}`} target="_blank" rel="noreferrer"
                      style={{ color: 'var(--text3)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {groupAsin} <ExternalLink size={12} />
                    </a>
                  )}
                  {groupMode === 'name' && <span className="pill">Name-Matching ±10 Tage</span>}
                </div>
              </div>
              <button className="btn btn-ghost" onClick={closeGroupModal} style={{ padding: '6px 8px' }}><X size={16} /></button>
            </div>

            <div className="modalBody">
              {groupMode === 'name' && (
                <div style={{ border: '1px solid rgba(251,191,36,0.35)', background: 'rgba(251,191,36,0.08)', color: 'var(--text2)', borderRadius: 10, padding: '10px 12px', marginBottom: 10, fontSize: 12 }}>
                  <strong>Achtung: Gruppierung über Produktname</strong> — keine ASIN, ±10 Tage Zeitraum.
                  {groupRange?.from && groupRange?.to && <div style={{ fontFamily: 'ui-monospace,Consolas,monospace', fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{formatDate(groupRange.from)} – {formatDate(groupRange.to)}</div>}
                </div>
              )}

              {groupOrders.length === 0 ? (
                <div style={{ padding: 20, color: 'var(--text3)', textAlign: 'center' }}>Keine Einträge gefunden.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
                    <button className="btn" onClick={selectAllGroup} style={{ border: '1px solid var(--border)' }}>Alle markieren</button>
                    <button className="btn" onClick={() => setSelectedGroup({})} style={{ border: '1px solid var(--border)' }}>Leeren</button>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--rv-font)', textTransform: 'uppercase' }}>Status:</span>
                      <select value={groupStatusToSet} onChange={e => setGroupStatusToSet(e.target.value)}
                        style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', color: 'var(--text2)', fontSize: 12, outline: 'none' }}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>

                  {groupOrders.map(o => {
                    const b = STATUS_BADGE[o.status] || STATUS_BADGE.Bestellt;
                    const disabled = isLockedStatus(o.status);
                    return (
                      <div key={o.id} className={`groupRow${disabled ? ' dim' : ''}`}>
                        <input type="checkbox" checked={!!selectedGroup[o.id]} onChange={() => toggleOrderGroup(o.id)}
                          disabled={disabled} style={{ width: 'auto', cursor: disabled ? 'not-allowed' : 'pointer' }} />
                        <div style={{ minWidth: 160 }}>
                          <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 700 }}>{o.account_name || '-'}</div>
                          <div style={{ fontFamily: 'ui-monospace,Consolas,monospace', fontSize: 11, color: 'var(--text3)' }}>{o.order_number || '-'} · {formatDate(o.order_date)}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: 'var(--text)' }}>{clampText(getProductName(o), 120)}</div>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                            <span style={{ background: b.bg, color: b.color, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>{b.label}</span>
                            <span style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 700 }}>{o.agent_name || '-'}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {o.review_url ? (
                            <>
                              <a href={o.review_url} target="_blank" rel="noreferrer"
                                style={{ color: 'var(--accent2)', textDecoration: 'none', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <ExternalLink size={14} />Review
                              </a>
                              <button className="btn btn-ghost" onClick={() => copyLink(o.review_url, o.id)} style={{ padding: '4px 8px' }}>
                                {copiedId === o.id ? '✓' : <Copy size={12} />}
                              </button>
                            </>
                          ) : <span style={{ color: 'var(--text3)', fontSize: 11, fontStyle: 'italic' }}>kein Link</span>}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            <div className="modalFooter">
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>{selectedCountGroup > 0 ? `${selectedCountGroup} ausgewählt` : 'Nichts ausgewählt'}</span>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn" onClick={copySelectedGroupLinks} disabled={!groupOrders.length} style={{ border: '1px solid var(--border)' }}>Ausgewählte Links</button>
                <button className="btn" onClick={copyAllGroupLinks} disabled={!groupOrders.length} style={{ border: '1px solid var(--border)' }}>Alle Links</button>
                <button className="btn" onClick={copyGroupTelegram} disabled={!groupOrders.length} style={{ border: '1px solid var(--border)' }}>📋 Alles kopieren</button>
                <button className="btn btn-primary" onClick={submitSelectedGroup} disabled={submitting || selectedCountGroup === 0} style={{ background: 'var(--accent3)', border: 'none' }}>
                  <Send size={13} />{submitting ? 'Wird gesetzt...' : 'Status setzen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {reviewChangeUndo && (
        <div style={{
          position: 'fixed', right: 22, bottom: 22, zIndex: 12000,
          display: 'flex', alignItems: 'center', gap: 12,
          background: 'var(--bg2)', color: 'var(--text)', border: '1px solid rgba(74,222,128,0.45)',
          borderRadius: 10, padding: '10px 12px', boxShadow: '0 12px 35px rgba(0,0,0,0.35)',
          fontSize: 12,
        }}>
          <span>✓ Review-Änderung geschlossen{reviewChangeUndo.orderNumber ? ` · ${reviewChangeUndo.orderNumber}` : ''}</span>
          <button className="btn" onClick={undoReviewChange} style={{ padding: '4px 9px', fontSize: 11, border: '1px solid var(--border)' }}>
            Rückgängig
          </button>
        </div>
      )}

      {noteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.target === e.currentTarget && setNoteModal(null)}>
          <div className="card" style={{ width: 420, padding: 24 }}>
            <div style={{ fontFamily: 'var(--rv-font)', fontWeight: 700, marginBottom: 4 }}><StickyNote size={16}/> Notiz</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>{noteModal.product_name}</div>
            <textarea aria-label="Notiztext" value={noteText} onChange={e => setNoteText(e.target.value)} rows={5}
              placeholder="z.B. Warte auf Lieferung, Review erst nächste Woche..."
              style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, color: 'var(--text1)', fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setNoteModal(null)} style={{ padding: '7px 16px', fontSize: 12, color: 'var(--text2)', border: '1px solid var(--border)', background: 'transparent' }}>Abbrechen</button>
              <button className="btn" onClick={saveNote} disabled={noteSaving}
                style={{ padding: '7px 16px', fontSize: 12, background: 'var(--accent)', color: 'white', border: 'none' }}>
                {noteSaving ? '...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
